import { createSlice } from "@reduxjs/toolkit";

import {
  createHusbandryTaskThunk,
  deleteHusbandryTaskThunk,
  fetchHusbandryQuickTaskThunk,
  fetchHusbandryTasksThunk,
  updateHusbandryTaskStatusThunk,
  updateHusbandryTaskThunk,
} from "../thunks/husbandryTasksThunks";

import type {
  HusbandryQuickTask,
  HusbandryTask,
  HusbandryTaskFilters,
  HusbandryTaskStatus,
} from "../../utils/types/husbandryTasks";
import type { PayloadAction } from "@reduxjs/toolkit";

interface HusbandryTasksState {
  tasks: HusbandryTask[];
  loading: boolean;
  error: string | null;
  filters: HusbandryTaskFilters;
  total: number;
  quickTask: HusbandryQuickTask[];
  quickTaskLoading: boolean;
  quickTaskError: string | null;
}

const initialState: HusbandryTasksState = {
  tasks: [],
  loading: false,
  error: null,
  filters: {
    search: "",
    myTasksOnly: false,
    vesselStatus: null,
  },
  total: 0,
  quickTask: [],
  quickTaskLoading: false,
  quickTaskError: null,
};

const husbandryTasksSlice = createSlice({
  name: "husbandryTasks",
  initialState,
  reducers: {
    setHusbandrySearchFilter(state, action: PayloadAction<string>) {
      state.filters.search = action.payload;
    },
    setHusbandryMyTasksOnly(state, action: PayloadAction<boolean>) {
      state.filters.myTasksOnly = action.payload;
    },
    setHusbandryVesselStatusFilter(state, action: PayloadAction<string | null>) {
      state.filters.vesselStatus = action.payload;
    },
    clearHusbandryFilters(state) {
      state.filters = {
        search: "",
        myTasksOnly: false,
        vesselStatus: null,
      };
    },
    moveHusbandryTask(
      state,
      action: PayloadAction<{ taskId: string; newStatus: HusbandryTaskStatus }>
    ) {
      const { taskId, newStatus } = action.payload;
      const task = state.tasks.find((t) => t.id === taskId);
      if (task) {
        task.status = newStatus;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch tasks
    builder
      .addCase(fetchHusbandryTasksThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchHusbandryTasksThunk.fulfilled,
        (state, action: PayloadAction<{ data: HusbandryTask[]; total: number }>) => {
          state.loading = false;
          state.tasks = action.payload.data;
          state.total = action.payload.total;
        }
      )
      .addCase(fetchHusbandryTasksThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create task
    builder
      .addCase(createHusbandryTaskThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createHusbandryTaskThunk.fulfilled,
        (state, action: PayloadAction<HusbandryTask>) => {
          state.loading = false;
          state.tasks.push(action.payload);
          state.total += 1;
        }
      )
      .addCase(createHusbandryTaskThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update task
    builder
      .addCase(updateHusbandryTaskThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(
        updateHusbandryTaskThunk.fulfilled,
        (state, action: PayloadAction<HusbandryTask>) => {
          const index = state.tasks.findIndex((t) => t.id === action.payload.id);
          if (index !== -1) {
            state.tasks[index] = action.payload;
          }
        }
      )
      .addCase(updateHusbandryTaskThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update task status
    builder
      .addCase(
        updateHusbandryTaskStatusThunk.fulfilled,
        (state, action: PayloadAction<HusbandryTask>) => {
          const index = state.tasks.findIndex((t) => t.id === action.payload.id);
          if (index !== -1) {
            state.tasks[index] = action.payload;
          }
        }
      )
      .addCase(updateHusbandryTaskStatusThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete task
    builder
      .addCase(
        deleteHusbandryTaskThunk.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.tasks = state.tasks.filter((t) => t.id !== action.payload);
          state.total -= 1;
        }
      )
      .addCase(deleteHusbandryTaskThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Fetch quick tasks
    builder
      .addCase(fetchHusbandryQuickTaskThunk.pending, (state) => {
        state.quickTaskLoading = true;
        state.quickTaskError = null;
      })
      .addCase(
        fetchHusbandryQuickTaskThunk.fulfilled,
        (state, action: PayloadAction<{ data: HusbandryQuickTask[] }>) => {
          state.quickTaskLoading = false;
          state.quickTask = action.payload.data;
        }
      )
      .addCase(fetchHusbandryQuickTaskThunk.rejected, (state, action) => {
        state.quickTaskLoading = false;
        state.quickTaskError = action.payload as string;
      });
  },
});

export const {
  setHusbandrySearchFilter,
  setHusbandryMyTasksOnly,
  setHusbandryVesselStatusFilter,
  clearHusbandryFilters,
  moveHusbandryTask,
} = husbandryTasksSlice.actions;

export default husbandryTasksSlice.reducer;
